import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import React from "react";
import { useColors } from "@inbody/core";
import { Box, Typography } from "@mui/material";
import { SectionCardHeader } from "./SectionCardHeader.js";
import { SectionHeaderBox } from "./SectionHeaderBox.js";
import { SectionPaper } from "./SectionPaper.js";
function SectionCard({
  title,
  icon,
  actions,
  defaultExpanded = true,
  expandable = true,
  titleInfo,
  titleInfoNode,
  showAdminLock,
  adminLockLabel,
  children,
  headerBgColor,
  headerBorderColor,
  headerMinHeight,
  minHeight,
  stickyHeader = false
}) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const { gray } = useColors();
  const sentinelRef = React.useRef(null);
  const [stuck, setStuck] = React.useState(false);
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!stickyHeader || !el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting)
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [stickyHeader]);
  const hasHeader = !!title || !!actions || !!icon || !!titleInfo || expandable || showAdminLock;
  const headerContent = /* @__PURE__ */ jsx(
    SectionCardHeader,
    {
      icon,
      title,
      collapsed: expandable,
      expanded,
      setExpanded,
      actions,
      titleInfo,
      titleInfoNode,
      showAdminLock,
      adminLockLabel
    }
  );
  const header = headerBgColor ? /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        px: 3,
        py: 2,
        minHeight: headerMinHeight,
        bgcolor: headerBgColor,
        borderBottom: `1px solid ${headerBorderColor || gray[200]}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: { mobile: "wrap", tablet: "nowrap" },
        rowGap: 1.5
      },
      children: headerContent
    }
  ) : /* @__PURE__ */ jsx(SectionHeaderBox, { minHeight: headerMinHeight, children: headerContent });
  return /* @__PURE__ */ jsxs(
    SectionPaper,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        // 부모 flex 안에서 내용 크기 아래로 줄어들 수 있어야 한다.
        minHeight: 0,
        maxHeight: "100%",
        // 카드 최소 높이.
        //
        // flex-basis 로 준다 — flex: 1 (basis: 0%) 이 걸려 있어 height 는 무시되고,
        // min-height 로 주면 축소 한계가 되어 공간이 부족해도 카드가 버티며 부모를
        // 넘친다(브라우저 확대 시 페이지네이션이 화면 밖으로 잘림).
        // basis 는 "이만큼에서 시작하되 모자라면 줄어든다" 라서 양쪽을 다 만족한다:
        // 내용이 적으면 이 높이를 확보하고, 남은 공간이 작으면 그만큼 줄어든다.
        ...minHeight != null && { flexBasis: minHeight },
        // overflow: hidden 은 카드를 스크롤 컨테이너로 만들어 sticky 헤더가 붙지 않고,
        // 헤더가 카드 테두리 위로 1px 겹쳐 그리는 것도 잘라 버린다.
        // 모서리 잘라내기는 헤더가 스스로 하므로 sticky 카드는 넘침을 그대로 둔다.
        ...stickyHeader && { overflow: "visible" }
      },
      children: [
        hasHeader && (stickyHeader ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Box, { ref: sentinelRef, sx: { height: "1px", mb: "-1px" } }),
          /* @__PURE__ */ jsx(
            Box,
            {
              sx: {
                position: "sticky",
                top: 0,
                zIndex: 2,
                // 카드 테두리 위로 1px씩 겹쳐, 헤더가 위·좌·우 테두리를 직접 그린다
                mx: "-1px",
                mt: "-1px",
                // 붙은 동안 둥근 모서리 바깥으로 본문이 비치지 않게 레이아웃 배경색으로 가린다
                bgcolor: stuck ? gray[25] : "transparent",
                transition: "box-shadow 0.15s",
                boxShadow: stuck ? "0 6px 8px -6px rgba(0, 0, 0, 0.18)" : "none"
              },
              children: /* @__PURE__ */ jsx(
                Box,
                {
                  sx: {
                    border: `1px solid ${gray[200]}`,
                    borderBottom: 0,
                    // 카드(SectionPaper borderRadius: 2)와 같은 둥근 위 모서리
                    borderRadius: (theme) => {
                      const r = Number(theme.shape.borderRadius) * 2;
                      return `${r}px ${r}px 0 0`;
                    },
                    overflow: "hidden"
                  },
                  children: header
                }
              )
            }
          )
        ] }) : header),
        expanded && /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0
            },
            children
          }
        )
      ]
    }
  );
}
function SubCategory({
  title,
  children,
  isLast,
  action,
  fill,
  minBodyHeight = 220
}) {
  const { brand, gray } = useColors();
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        mb: isLast ? 0 : 3,
        pb: isLast ? 0 : 2.5,
        borderBottom: isLast ? "none" : `1px solid ${gray[200]}`,
        ...fill && {
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column"
        }
      },
      children: [
        /* @__PURE__ */ jsxs(
          Box,
          {
            sx: {
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              minHeight: 28,
              flexShrink: 0
            },
            children: [
              /* @__PURE__ */ jsx(
                Typography,
                {
                  variant: "caption",
                  sx: {
                    fontWeight: 600,
                    color: brand[600],
                    pl: 1.25,
                    borderLeft: `3px solid ${brand[600]}`
                  },
                  children: title
                }
              ),
              action
            ]
          }
        ),
        fill ? /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              flex: 1,
              minHeight: minBodyHeight,
              overflow: "auto"
            },
            children
          }
        ) : children
      ]
    }
  );
}
export {
  SectionCard,
  SubCategory
};
